// Atomic Mode overlay. When active it covers the 2D sandbox canvas and walks
// the user through two phases:
//
//   1. "select" — drag a rectangle to choose a hotspot. The sim is paused
//      (App pauses it on entry), so the layout under the box is stable.
//   2. "run" — the selected materials are converted to a LAMMPS system and a
//      wrapped `atomify` is mounted in an iframe over the canvas, running the
//      molecular-dynamics equivalent of that hotspot.
//
// LICENSE BOUNDARY: atomify (GPL-3.0) only ever runs inside the iframe, fed by
// a URL we build in our own MIT/Apache code (`sim/atomicMode.ts`). Nothing
// here imports atomify source.

import { useEffect, useMemo, useRef, useState } from "react";

import {
  ATOMIFY_BASE_URL,
  buildAtomicModeRun,
  snapshotSandbox,
} from "../sim/atomicMode";
import {
  describeEmbedEvent,
  subscribeEmbedEvents,
  type AtomifyEmbedEvent,
  type LoadSimulationMessage,
} from "../sim/atomifyEmbed";
import { api, ApiError, type AtomicRunSummary } from "../auth/client";
import type { GridRegion } from "../sim/lammps";
import { diffusionCoefficient, picosecondsPerStep } from "../sim/measurements";
import type { MaterialDef, SandboxLike } from "../sim/types";

// If the iframe never reports `ready` within this window, assume it failed to
// load (blocked, offline, wrong URL) and surface a hint instead of hanging.
const READY_TIMEOUT_MS = 15000;

interface LogLine {
  t: number;
  text: string;
  kind: "info" | "error" | "reaction" | "insight" | "raw";
}

// Scrollback cap for the log feed. Generous because the raw LAMMPS console
// (thermo table every N steps + run setup) streams many lines per run.
const MAX_LOG_LINES = 400;

// Live readout derived from the simulation's measurement stream.
interface LiveMetrics {
  step: number;
  temp: number | null;
  pe: number | null;
  /** PE change since the first dynamics sample (negative = energy released). */
  deltaPe: number | null;
  /** Self-diffusion coefficient (Å²/ps), or null until the fit is meaningful. */
  d: number | null;
  /** Latest mean-squared displacement total (Å²). */
  msd: number | null;
  numAtoms: number;
}

// Format an energy delta with its unit (eV for metal runs, kcal/mol for real).
function formatEnergy(x: number, unit: string): string {
  const a = Math.abs(x);
  const s = a >= 1000 ? a.toFixed(0) : a >= 1 ? a.toFixed(1) : a.toPrecision(2);
  return `${x < 0 ? "−" : ""}${s} ${unit}`;
}

// A run can form/break chemical bonds only with a bonding potential
// (ReaxFF / AIREBO / the hybrid carbon path). LJ/EAM runs have no chemistry, so
// we never flag a "reaction" for them — a PE dip there is just equilibration.
function isReactiveRun(recipeId: string | null): boolean {
  return !!recipeId && /reax|airebo|hybrid_carbon/.test(recipeId);
}

interface AtomicViewProps {
  active: boolean;
  sandbox: SandboxLike | null;
  materials: MaterialDef[];
  /** Grid resolution of the viewed world (square). */
  worldSize: number;
  /** Leave Atomic Mode (also unpauses, handled by App). */
  onExit: () => void;
}

type Phase = "select" | "run";

interface RunState {
  url: string;
  message: LoadSimulationMessage;
  atomCount: number;
  elementSymbols: string[];
  recipeId: string | null;
  /** Display names of the distinct materials in the selection (for the insight). */
  materialNames: string[];
}

// A normalized drag rectangle in 0..1 of the overlay, kept as start/now so
// the box can be drawn in either direction.
interface DragBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

// Add/replace a cache-busting `_r` param so re-assigning the iframe src forces
// a full reload of the bundle.
function bumpReload(url: string): string {
  try {
    const u = new URL(url);
    u.searchParams.set("_r", String(Date.now()));
    return u.toString();
  } catch {
    return url + (url.includes("?") ? "&" : "?") + "_r=" + Date.now();
  }
}

// Human label for the run-phase status chip / loading veil.
function statusLabel(status: AtomifyEmbedEvent["type"] | "starting"): string {
  switch (status) {
    case "starting":
      return "loading engine…";
    case "ready":
      return "starting engine…";
    case "wasm-ready":
      return "engine ready, loading system…";
    case "loaded":
      return "system loaded, starting run…";
    case "running":
      return "running";
    case "stopped":
      return "finished";
    case "error":
      return "error";
    default:
      return "working…";
  }
}

function normalize(b: DragBox): DragBox {
  return {
    x0: Math.min(b.x0, b.x1),
    y0: Math.min(b.y0, b.y1),
    x1: Math.max(b.x0, b.x1),
    y1: Math.max(b.y0, b.y1),
  };
}

export function AtomicView(props: AtomicViewProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const logBodyRef = useRef<HTMLDivElement | null>(null);
  const [phase, setPhase] = useState<Phase>("select");
  const [drag, setDrag] = useState<DragBox | null>(null);
  const [dragging, setDragging] = useState(false);
  const [run, setRun] = useState<RunState | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Run-phase status: where the iframe is in its lifecycle, plus a live log.
  const [status, setStatus] = useState<AtomifyEmbedEvent["type"] | "starting">(
    "starting",
  );
  const [log, setLog] = useState<LogLine[]>([]);
  const [runError, setRunError] = useState<string | null>(null);
  // Live measurement readout + the (sticky) reaction flag.
  const [metrics, setMetrics] = useState<LiveMetrics | null>(null);
  const [reaction, setReaction] = useState<string | null>(null);
  // AI-written interpretation of the finished run (null until it lands).
  const [insight, setInsight] = useState<string | null>(null);
  const [insightPending, setInsightPending] = useState(false);
  const [insightError, setInsightError] = useState<string | null>(null);

  // Per-run accumulators for the measurement stream (reset on each new run).
  const pe0Ref = useRef<number | null>(null);
  const psPerStepRef = useRef<number | null>(null);
  const energyUnitRef = useRef<string>("eV");
  const msdSeriesRef = useRef<{ t: number; msd: number }[]>([]);
  const reactiveRef = useRef(false);
  const reactionFlaggedRef = useRef(false);
  // Latest sample (a ref so the run-completion handler reads it synchronously)
  // and a once-guard so we request the AI insight a single time per run.
  const latestRef = useRef<LiveMetrics | null>(null);
  const insightDoneRef = useRef(false);

  const appendLog = (text: string, kind: LogLine["kind"] = "info") => {
    setLog((prev) =>
      [...prev, { t: Date.now(), text, kind }].slice(-MAX_LOG_LINES),
    );
  };

  // Append a batch of raw LAMMPS console lines in one update (the engine emits
  // them in bursts), trimming trailing whitespace and dropping blank lines.
  const appendRawLines = (lines: string[]) => {
    const cleaned = lines
      .map((l) => l.replace(/\s+$/, ""))
      .filter((l) => l.length > 0);
    if (cleaned.length === 0) return;
    const now = Date.now();
    setLog((prev) =>
      [
        ...prev,
        ...cleaned.map((text) => ({ t: now, text, kind: "raw" as const })),
      ].slice(-MAX_LOG_LINES),
    );
  };

  // Reset the measurement accumulators + readout for a fresh run.
  const resetMeasurements = (inputScript: string, recipeId: string | null) => {
    pe0Ref.current = null;
    msdSeriesRef.current = [];
    reactionFlaggedRef.current = false;
    latestRef.current = null;
    insightDoneRef.current = false;
    psPerStepRef.current = picosecondsPerStep(inputScript);
    const units = /^\s*units\s+(\w+)/m.exec(inputScript)?.[1] ?? "metal";
    energyUnitRef.current = units === "real" ? "kcal/mol" : "eV";
    reactiveRef.current = isReactiveRun(recipeId);
    setMetrics(null);
    setReaction(null);
    setInsight(null);
    setInsightPending(false);
    setInsightError(null);
  };

  // Fold one measurement sample into the live readout: update T/PE, fit the
  // diffusion coefficient from the MSD series, and raise the reaction flag the
  // first time a bonding run releases significant energy.
  const handleMeasurement = (
    m: Extract<AtomifyEmbedEvent, { type: "measurement" }>,
  ) => {
    if (pe0Ref.current === null && m.pe !== null) pe0Ref.current = m.pe;
    const pe0 = pe0Ref.current;
    const deltaPe = pe0 !== null && m.pe !== null ? m.pe - pe0 : null;

    let d: number | null = null;
    const ps = psPerStepRef.current;
    if (m.msd !== null && ps !== null) {
      msdSeriesRef.current.push({ t: m.step * ps, msd: m.msd });
      if (msdSeriesRef.current.length > 500) msdSeriesRef.current.shift();
      d = diffusionCoefficient(msdSeriesRef.current);
    }
    const live: LiveMetrics = {
      step: m.step,
      temp: m.temp,
      pe: m.pe,
      deltaPe,
      d,
      msd: m.msd,
      numAtoms: m.numAtoms,
    };
    latestRef.current = live;
    setMetrics(live);

    // Reaction: a bonding run whose PE drops well past its starting value means
    // bonds formed (energy released). Flag once; show the magnitude so it's
    // transparent rather than a black-box claim.
    if (
      !reactionFlaggedRef.current &&
      reactiveRef.current &&
      pe0 !== null &&
      m.pe !== null
    ) {
      const drop = pe0 - m.pe; // positive when energy was released
      if (drop > Math.max(1e-6, 0.08 * Math.abs(pe0))) {
        reactionFlaggedRef.current = true;
        const txt = `⚛ REACTION DETECTED — bonds forming, energy released (ΔPE ${formatEnergy(-drop, energyUnitRef.current)})`;
        setReaction(txt);
        appendLog(txt, "reaction");
      }
    }
  };

  // When the run finishes, ask the API to turn the measured observables into a
  // concise plain-text explanation (physisorption vs. bonding, diffusion,
  // stability) for the neon readout. Fires once; degrades quietly if the AI
  // endpoint is unavailable or the user isn't signed in.
  const requestInsight = (r: RunState) => {
    if (insightDoneRef.current) return;
    insightDoneRef.current = true;
    const m = latestRef.current;
    const pe0 = pe0Ref.current;
    const summary: AtomicRunSummary = {
      materials: r.materialNames,
      elements: r.elementSymbols,
      interactionModel: r.recipeId ?? "UFF Lennard-Jones",
      atomCount: r.atomCount,
      energyUnit: energyUnitRef.current,
      temperatureK: m?.temp ?? null,
      steps: m?.step ?? 0,
      peStart: pe0,
      peEnd: m?.pe ?? null,
      deltaPe: pe0 != null && m?.pe != null ? m.pe - pe0 : null,
      msdEnd: m?.msd ?? null,
      diffusion: m?.d ?? null,
      reactionDetected: reactionFlaggedRef.current,
    };
    setInsight(null);
    setInsightError(null);
    setInsightPending(true);
    api
      .atomicInsight(summary)
      .then((res) => {
        setInsight(res.insight);
      })
      .catch((err) => {
        // Show a short reason in the insight bar rather than failing silently.
        const code = err instanceof ApiError ? err.code : String(err);
        setInsightError(
          code === "no_token" || code === "unauthorized"
            ? "Sign in to get an AI interpretation of this run."
            : code === "ai_unavailable"
              ? "AI interpretation is unavailable on this deployment."
              : "Couldn’t reach Coscience for an interpretation.",
        );
      })
      .finally(() => setInsightPending(false));
  };

  // Reset whenever the mode is toggled off so the next entry starts clean.
  const wasActive = useRef(false);
  if (props.active !== wasActive.current) {
    wasActive.current = props.active;
    if (!props.active) {
      // Defer state resets to avoid setState-in-render; cheap on toggle.
      queueMicrotask(() => {
        setPhase("select");
        setDrag(null);
        setDragging(false);
        setRun(null);
        setError(null);
        setStatus("starting");
        setLog([]);
        setRunError(null);
        setMetrics(null);
        setReaction(null);
        setInsight(null);
        setInsightPending(false);
        setInsightError(null);
      });
    }
  }

  // While the iframe is running: listen for atomify's status messages and push
  // the simulation in once. The bundle is large (~11 MB) and slow to boot, so
  // we (re)send a few times — on the bridge's "ready" announcement and on a
  // bounded interval — but STOP as soon as the sim is confirmed loaded. We must
  // NOT resend on every "log" event: the bridge logs on each receive, which
  // would create a send⇄log feedback loop that re-dispatches the run forever.
  useEffect(() => {
    if (phase !== "run" || !run) return;
    let gotAnything = false;
    let loaded = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 8; // ~12 s of retries before giving up

    const sendSimulation = () => {
      if (loaded) return;
      const frame = iframeRef.current?.contentWindow;
      if (!frame) return;
      attempts++;
      // Target "*" — the bundle is on another origin; the iframe validates the
      // message `source`. Payload is non-sensitive simulation text.
      frame.postMessage(run.message, "*");
    };

    const unsubscribe = subscribeEmbedEvents((event) => {
      gotAnything = true;
      // Live data: fold into the neon readout, don't treat as a lifecycle event
      // (no status change, no plain log line, no resend).
      if (event.type === "measurement") {
        handleMeasurement(event);
        return;
      }
      // Raw LAMMPS console: stream verbatim into the feed.
      if (event.type === "raw-log") {
        appendRawLines(event.lines);
        return;
      }
      // Push only when the bridge signals it can receive (ready/wasm-ready).
      // Never on "log" — that would loop against the bridge's receive logs.
      if (event.type === "loaded" || event.type === "running") {
        loaded = true;
      } else if (event.type === "ready" || event.type === "wasm-ready") {
        sendSimulation();
      } else if (event.type === "stopped") {
        // Only at the TRUE end of dynamics. The bridge posts a "stopped" at
        // mount too (running starts false), before any steps run — ignore it,
        // or the insight fires with empty data. Require real measured steps.
        if ((latestRef.current?.step ?? 0) > 0) requestInsight(run);
      }
      setStatus(event.type);
      if (event.type === "error") {
        setRunError(event.message);
        appendLog(describeEmbedEvent(event), "error");
      } else {
        appendLog(describeEmbedEvent(event));
      }
    }, iframeRef.current?.contentWindow ?? undefined);

    // Bounded retry: covers a slow boot where our send lands before the
    // bridge's listener is mounted. Stops on `loaded` or after MAX_ATTEMPTS.
    const retry = window.setInterval(() => {
      if (loaded || attempts >= MAX_ATTEMPTS) {
        window.clearInterval(retry);
        return;
      }
      sendSimulation();
    }, 1500);

    const watchdog = window.setTimeout(() => {
      if (!gotAnything) {
        appendLog(
          "Still no response from the Atomify engine — it may be blocked or offline. It can take a while to load the first time; if it stays blank, check your connection.",
          "error",
        );
      }
    }, READY_TIMEOUT_MS);

    return () => {
      unsubscribe();
      window.clearInterval(retry);
      window.clearTimeout(watchdog);
    };
    // Re-bind when a new run starts or the frame reloads (new message/url) so
    // we target the fresh frame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, run?.message, run?.url]);

  // Keep the streaming log pinned to the latest line (live-console feel).
  useEffect(() => {
    const el = logBodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [log]);

  // The selection must map to the actual simulation grid, which is drawn on
  // the sim <canvas> — NOT this overlay (the overlay covers the whole column,
  // including the toolbar, so its rect is larger/offset). Find the canvas and
  // map pointer positions against ITS rect so the box matches the cells read.
  const canvasRect = (): DOMRect | null => {
    const el = document.querySelector<HTMLCanvasElement>(
      ".canvas-host canvas.sim",
    );
    if (el) return el.getBoundingClientRect();
    // Fallback: the overlay layer itself, so selection still works (just less
    // precisely) if the canvas can't be found.
    return hostRef.current?.getBoundingClientRect() ?? null;
  };

  const fracToCell = (fx: number, fy: number): [number, number] => {
    const n = props.worldSize;
    return [
      Math.max(0, Math.min(n - 1, Math.floor(fx * n))),
      Math.max(0, Math.min(n - 1, Math.floor(fy * n))),
    ];
  };

  // Pointer → fraction (0..1) of the grid canvas, clamped to the canvas.
  const pointerFrac = (clientX: number, clientY: number): [number, number] => {
    const rect = canvasRect();
    if (!rect || rect.width === 0 || rect.height === 0) return [0, 0];
    return [
      Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)),
      Math.max(0, Math.min(1, (clientY - rect.top) / rect.height)),
    ];
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (phase !== "select") return;
    // Only start a drag on the bare selection layer — never when the press
    // lands on the toolbar/buttons sitting on top of it (otherwise pressing
    // "Run" would reset the selection to a zero-size box and disable itself).
    if (e.target !== e.currentTarget) return;
    const [fx, fy] = pointerFrac(e.clientX, e.clientY);
    setDrag({ x0: fx, y0: fy, x1: fx, y1: fy });
    setDragging(true);
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging || !drag) return;
    const [fx, fy] = pointerFrac(e.clientX, e.clientY);
    setDrag({ ...drag, x1: fx, y1: fy });
  };

  const onPointerUp = () => {
    if (!dragging) return;
    setDragging(false);
  };

  const region: GridRegion | null = useMemo(() => {
    if (!drag) return null;
    const n = normalize(drag);
    // Ignore tiny accidental drags.
    if (n.x1 - n.x0 < 0.01 && n.y1 - n.y0 < 0.01) return null;
    const [x0, y0] = fracToCell(n.x0, n.y0);
    const [x1, y1] = fracToCell(n.x1, n.y1);
    return { x0, y0, x1, y1 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag, props.worldSize]);

  const cellCount = region
    ? (region.x1 - region.x0 + 1) * (region.y1 - region.y0 + 1)
    : 0;

  const handleRun = () => {
    setError(null);
    const sandbox = props.sandbox;
    if (!sandbox) {
      setError("Sandbox isn't ready yet — give it a moment and try again.");
      return;
    }
    if (!region) {
      setError("Drag a box over the canvas first to choose a region.");
      return;
    }
    try {
      const snapshot = snapshotSandbox(sandbox);
      const result = buildAtomicModeRun(snapshot, region, props.materials);
      if (!result) {
        setError(
          "No atoms in that selection — it's empty or only holds materials without element data. Try a region with solids or liquids.",
        );
        return;
      }
      // Fresh run: reset status/log/measurements before mounting the iframe.
      setStatus("starting");
      setRunError(null);
      setLog([]);
      resetMeasurements(result.inputScript, result.recipeId);
      appendLog(
        `Converted ${cellCount} cells → ${result.atomCount.toLocaleString()} atoms (${result.elementSymbols.join(", ") || "—"}).`,
      );
      appendLog(
        result.recipeId
          ? `Potential: ${result.recipeId} (${result.potentialFiles.join(", ")}).`
          : "Potential: generic Lennard-Jones (UFF) — no specialised potential for this material.",
      );
      appendLog("Loading Atomify (LAMMPS engine) in the simulation frame…");
      // Distinct material names under the selection — labels the insight.
      const matNames = new Set<string>();
      for (let gy = region.y0; gy <= region.y1; gy++) {
        for (let gx = region.x0; gx <= region.x1; gx++) {
          const mat =
            props.materials[snapshot.materials[gy * snapshot.width + gx]];
          if (mat && mat.id !== "empty") matNames.add(mat.name);
        }
      }
      setRun({
        url: result.url,
        message: result.message,
        atomCount: result.atomCount,
        elementSymbols: result.elementSymbols,
        recipeId: result.recipeId,
        materialNames: [...matNames].slice(0, 16),
      });
      setPhase("run");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (!props.active) return null;

  // Selection rectangle, drawn over the actual canvas (fixed positioning in
  // client coords) so it lines up exactly with the cells the converter reads.
  const box = drag ? normalize(drag) : null;
  const rect = box ? canvasRect() : null;
  const boxStyle: React.CSSProperties | null =
    box && rect
      ? {
          position: "fixed",
          left: `${rect.left + box.x0 * rect.width}px`,
          top: `${rect.top + box.y0 * rect.height}px`,
          width: `${(box.x1 - box.x0) * rect.width}px`,
          height: `${(box.y1 - box.y0) * rect.height}px`,
        }
      : null;

  return (
    <div className="atomic-host">
      {phase === "select" && (
        <div
          ref={hostRef}
          className="atomic-select-layer"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          {boxStyle && <div className="atomic-select-box" style={boxStyle} />}

          <div
            className="atomic-toolbar"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <span className="atomic-badge" aria-hidden="true">
              ⚛
            </span>
            <span className="atomic-title">Atomic Mode</span>
            <span className="atomic-instr">
              {region
                ? `Hotspot: ${region.x1 - region.x0 + 1}×${region.y1 - region.y0 + 1} cells (${cellCount})`
                : "Drag a box over the area to simulate atom-by-atom"}
            </span>
            <button
              type="button"
              className="atomic-run"
              disabled={!region}
              onClick={handleRun}
            >
              Run molecular dynamics →
            </button>
            <button
              type="button"
              className="atomic-exit"
              onClick={props.onExit}
            >
              ✕ Exit
            </button>
          </div>

          {error && <div className="atomic-error">{error}</div>}
        </div>
      )}

      {phase === "run" && run && (
        <div className="atomic-run-layer">
          <iframe
            ref={iframeRef}
            className="atomic-iframe"
            title="Atomify molecular dynamics"
            src={run.url}
            // Served from a different origin (Cloudflare Pages). The LAMMPS
            // build is single-threaded (no SharedArrayBuffer), so plain
            // scripts + same-origin-to-itself is enough.
            sandbox="allow-scripts allow-same-origin allow-downloads allow-popups"
            onLoad={() => appendLog("Simulation frame document loaded.")}
          />

          {/* Loading veil until atomify reports the run has started. */}
          {status !== "running" &&
            status !== "stopped" &&
            status !== "error" && (
              <div className="atomic-loading">
                <div className="atomic-spinner" aria-hidden="true" />
                <div className="atomic-loading-text">{statusLabel(status)}</div>
                <div className="atomic-loading-sub">
                  {run.atomCount.toLocaleString()} atoms ·{" "}
                  {run.elementSymbols.join(", ") || "—"}
                </div>
              </div>
            )}

          {runError && (
            <div className="atomic-error atomic-error-run">
              LAMMPS error: {runError}
            </div>
          )}

          {/* Live log panel: three independently-scrolling regions — the pink
              measurement readout (top), the raw LAMMPS feed (middle), and the
              insight rectangle (bottom). */}
          <div className="atomic-log" role="log">
            <div className="atomic-log-head">Atomic Mode log</div>

            {/* Live measurement readout — its own region, solid white so the
                raw feed never shows behind it; scrolls on its own if needed. */}
            {(metrics || reaction) && (
              <div className="atomic-metrics">
                {metrics && (
                  <>
                    <div className="atomic-metric">
                      step {metrics.step.toLocaleString()}
                      {metrics.temp !== null
                        ? ` · T ${metrics.temp.toFixed(0)} K`
                        : ""}
                    </div>
                    {metrics.pe !== null && (
                      <div className="atomic-metric">
                        PE {formatEnergy(metrics.pe, energyUnitRef.current)}
                        {metrics.deltaPe !== null
                          ? ` (Δ ${formatEnergy(metrics.deltaPe, energyUnitRef.current)})`
                          : ""}
                      </div>
                    )}
                    {metrics.d !== null && (
                      <div className="atomic-metric">
                        D {metrics.d.toFixed(3)} Å²/ps ·{" "}
                        {(metrics.d * 1e-8).toExponential(2)} m²/s
                      </div>
                    )}
                  </>
                )}
                {reaction && (
                  <div className="atomic-metric atomic-metric-reaction">
                    {reaction}
                  </div>
                )}
              </div>
            )}

            <div className="atomic-log-body" ref={logBodyRef}>
              {log.length === 0 && (
                <div className="atomic-log-line">Starting…</div>
              )}
              {log.map((line, i) => (
                <div
                  key={i}
                  className={
                    "atomic-log-line" +
                    (line.kind === "error" ? " atomic-log-error" : "") +
                    (line.kind === "reaction" ? " atomic-log-reaction" : "") +
                    (line.kind === "insight" ? " atomic-log-insight" : "") +
                    (line.kind === "raw" ? " atomic-log-raw" : "")
                  }
                >
                  {line.text}
                </div>
              ))}
            </div>

            {/* Insight rectangle — its own scrollable region at the bottom,
                white background, shown only at the end of the run. */}
            {(insightPending || insight || insightError) && (
              <div className="atomic-insight-bar">
                {insight ? (
                  <div className="atomic-insight-text">⚛ {insight}</div>
                ) : insightError ? (
                  <div className="atomic-insight-text atomic-insight-fail">
                    ⚠ {insightError}
                  </div>
                ) : (
                  <div className="atomic-insight-loading">
                    <span className="atomic-insight-dots" aria-hidden="true">
                      <i />
                      <i />
                      <i />
                    </span>
                    Coscience is processing your experiment…
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="atomic-run-toolbar">
            <span className="atomic-badge" aria-hidden="true">
              ⚛
            </span>
            <span className="atomic-title">
              Atomic Mode — {statusLabel(status)}
            </span>
            <span className="atomic-instr">
              {run.atomCount.toLocaleString()} atoms ·{" "}
              {run.elementSymbols.join(", ") || "—"}
            </span>
            <button
              type="button"
              className="atomic-run"
              title="Reload the engine and resend this simulation"
              onClick={() => {
                // Re-mount the iframe with a fresh cache-busting url and resend
                // (the simulation travels by postMessage, so it can't be
                // carried to a separate tab — reloading in place is the retry).
                setStatus("starting");
                setRunError(null);
                appendLog("Reloading engine…");
                setRun((prev) =>
                  prev ? { ...prev, url: bumpReload(prev.url) } : prev,
                );
              }}
            >
              ⟳ Reload engine
            </button>
            <button
              type="button"
              className="atomic-run"
              onClick={() => {
                setRun(null);
                setPhase("select");
              }}
            >
              ← New selection
            </button>
            <button
              type="button"
              className="atomic-exit"
              onClick={props.onExit}
            >
              ✕ Exit
            </button>
          </div>
        </div>
      )}

      {/* Tiny provenance note — atomify is GPL-3.0, served standalone. */}
      <a
        className="atomic-credit"
        href={ATOMIFY_BASE_URL}
        target="_blank"
        rel="noreferrer"
      >
        powered by Atomify (LAMMPS · GPL-3.0)
      </a>
    </div>
  );
}
